import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import useIsMobile from '../hooks/useIsMobile';
import {
  Alert,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Autocomplete,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
// Removed MUI X date pickers due to version compatibility issues
import { 
  Search as SearchIcon, 
  AutoFixHigh as TailorIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Visibility as VisibilityIcon,
  Assignment as AssignmentIcon,
  Send as SendIcon,
  MenuBook as MenuBookIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import IconButton from '@mui/material/IconButton';
import './AssignmentDialog.css';
import TailorAssignmentDialog from './TailorAssignmentDialog';
import BulkTailorDialog from './BulkTailorDialog';

interface Student {
  id: number;
  first_name: string;
  last_name: string;
  grade_level: string;
  full_name: string;
  cultures: string[];
  culture_codes: string[];
  email?: string;
  student_id?: string;
  reading_level?: number;
  primary_language?: string;
}

interface ClassData {
  id: number;
  name: string;
  subject: string | null;
  grade_level: string | null;
  student_count: number;
  google_classroom_id: string | null;
  students?: Student[];
}

interface Culture {
  id: number;
  code: string;
  name: string;
  description?: string;
}

interface Document {
  id: number;
  name: string;
}

interface AssignmentDialogProps {
  open: boolean;
  onClose: () => void;
  document: Document | null;
  onAssign: (assignmentData: any) => Promise<void>;
}

const AssignmentDialog: React.FC<AssignmentDialogProps> = ({ 
  open, 
  onClose, 
  document, 
  onAssign 
}) => {
  const { t } = useTranslation();
  const { token, isAuthenticated } = useAuth();
  const { isMobile } = useIsMobile();

  // State
  const [classes, setClasses] = useState<ClassData[]>([]);
  const [cultures, setCultures] = useState<Culture[]>([]);
  const [loading, setLoading] = useState(false);
  
  // Assignment state - only class-based assignments now
  const [selectedClass, setSelectedClass] = useState<number | null>(null);
  const [classStudents, setClassStudents] = useState<Student[]>([]);
  const [selectedStudents, setSelectedStudents] = useState<number[]>([]);
  const [dueDate, setDueDate] = useState<string>('');
  const [assignmentType, setAssignmentType] = useState<'submittable' | 'informational'>('submittable');
  
  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCultures, setSelectedCultures] = useState<Culture[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  
  // Tailor assignment state
  const [tailorDialogOpen, setTailorDialogOpen] = useState(false);
  const [selectedStudentForTailor, setSelectedStudentForTailor] = useState<Student | null>(null);
  const [tailoringStatus, setTailoringStatus] = useState<{[key: number]: any}>({});
  const [loadingTailoringStatus, setLoadingTailoringStatus] = useState(false);
  const [thumbnails, setThumbnails] = useState<{[key: number]: string | null}>({});
  const [loadingThumbnails, setLoadingThumbnails] = useState<{[key: number]: boolean}>({});
  
  // Bulk tailor state
  const [bulkTailorDialogOpen, setBulkTailorDialogOpen] = useState(false);
  
  // Interactive preview state
  const [interactivePreviewOpen, setInteractivePreviewOpen] = useState(false);
  const [interactivePreviewPages, setInteractivePreviewPages] = useState<any[]>([]);
  const [interactivePreviewName, setInteractivePreviewName] = useState('');
  
  // Success confirmation state
  const [assignmentSuccess, setAssignmentSuccess] = useState<{
    studentCount: number;
    className: string;
  } | null>(null);
  
  // Existing assignment warning state
  const [existingAssignment, setExistingAssignment] = useState<{
    class_name: string;
    student_count: number;
    class_id: number;
  } | null>(null);
  
  // Get the list of students to display - only from selected class
  const availableStudents = classStudents;
  
  // Filter students based on search and culture
  const filteredStudents = availableStudents.filter(student => {
    const matchesSearch = !searchQuery || 
      student.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      student.student_id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      student.email?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesCulture = selectedCultures.length === 0 || 
      selectedCultures.every(culture => 
        student.culture_codes.includes(culture.code) ||
        student.cultures.some(studentCulture => 
          studentCulture.toLowerCase().includes(culture.name.toLowerCase())
        )
      );
    
    return matchesSearch && matchesCulture;
  });

  // Fetch data
  useEffect(() => {
    if (open && token && isAuthenticated) {
      fetchClasses();
      fetchCultures();
      fetchExistingAssignment();
    }
  }, [open, token, isAuthenticated]);

  // Fetch class students when class changes
  useEffect(() => {
    if (selectedClass) {
      fetchClassStudents(selectedClass);
    }
  }, [selectedClass, token, isAuthenticated]);

  // Update select all when filtered students change
  useEffect(() => {
    setSelectAll(
      filteredStudents.length > 0 && 
      filteredStudents.every(student => selectedStudents.includes(student.id))
    );
  }, [filteredStudents, selectedStudents]);


  const fetchClasses = async () => {
    try {
      const response = await fetch('/api/classes', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) throw new Error('Failed to fetch classes');
      
      const data = await response.json();
      if (data.success) {
        setClasses(data.classes);
      }
    } catch (error) {
      console.error('Error fetching classes:', error);
    }
  };

  const fetchCultures = async () => {
    try {
      const response = await fetch('/api/students/cultures', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) throw new Error('Failed to fetch cultures');
      
      const data = await response.json();
      if (data.cultures) {
        setCultures(data.cultures);
      }
    } catch (error) {
      console.error('Error fetching cultures:', error);
    }
  };

  const fetchExistingAssignment = async () => {
    if (!document || !token) return;
    try {
      const response = await fetch(`/api/documents/${document.id}/assignments`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) return;
      const data = await response.json();
      if (data.success && data.assignments && data.assignments.length > 0) {
        const assignment = data.assignments[0];
        setExistingAssignment({
          class_name: assignment.class_name || 'Unknown class',
          student_count: assignment.student_ids?.length || 0,
          class_id: assignment.class_id,
        });
        if (assignment.class_id) {
          setSelectedClass(assignment.class_id);
        }
        if (assignment.assignment_type) {
          setAssignmentType(assignment.assignment_type);
        }
        if (assignment.due_date) {
          const d = new Date(assignment.due_date);
          const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000)
            .toISOString().slice(0, 16);
          setDueDate(local);
        }
      } else {
        setExistingAssignment(null);
      }
    } catch (error) {
      console.error('Error fetching existing assignment:', error);
    }
  };

  const fetchClassStudents = async (classId: number) => {
    try {
      const response = await fetch(`/api/classes/${classId}/students`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) throw new Error('Failed to fetch class students');
      
      const data = await response.json();
      if (data.success) {
        setClassStudents(data.students);
        // Auto-select all students in the class
        const studentIds = data.students.map((student: Student) => student.id);
        setSelectedStudents(studentIds);
        
        // Fetch tailoring status for this document and class
        await fetchTailoringStatus(classId);
      }
    } catch (error) {
      console.error('Error fetching class students:', error);
    }
  };

  const fetchTailoringStatus = async (classId: number) => {
    if (!token || !document) return;
    
    try {
      setLoadingTailoringStatus(true);
      const response = await fetch(`/api/documents/${document.id}/tailoring-status?class_id=${classId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) throw new Error('Failed to fetch tailoring status');
      
      const data = await response.json();
      if (data.success) {
        // Convert array to object keyed by student_id for easy lookup
        const statusMap: {[key: number]: any} = {};
        data.tailoring_status.forEach((status: any) => {
          statusMap[status.student_id] = status;
        });
        setTailoringStatus(statusMap);
      }
    } catch (error) {
      console.error('Error fetching tailoring status:', error);
    } finally {
      setLoadingTailoringStatus(false);
    }
  };

  const fetchThumbnail = async (studentId: number, tailoredDocId: number) => {
    if (!token || thumbnails[studentId] !== undefined) return;
    
    setLoadingThumbnails(prev => ({ ...prev, [studentId]: true }));
    
    try {
      const response = await fetch(`/api/tailored-documents/${tailoredDocId}/thumbnail`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) throw new Error('Failed to fetch thumbnail');
      
      const data = await response.json();
      if (data.success && data.thumbnail) {
        setThumbnails(prev => ({ ...prev, [studentId]: data.thumbnail }));
      } else {
        setThumbnails(prev => ({ ...prev, [studentId]: null }));
      }
    } catch (error) {
      console.error('Error fetching thumbnail:', error);
      setThumbnails(prev => ({ ...prev, [studentId]: null }));
    } finally {
      setLoadingThumbnails(prev => ({ ...prev, [studentId]: false }));
    }
  };

  // Fetch thumbnails when tailoring status changes
  React.useEffect(() => {
    Object.entries(tailoringStatus).forEach(([studentId, status]) => {
      if (status.has_tailored && status.tailored_document?.id) {
        fetchThumbnail(parseInt(studentId), status.tailored_document.id);
      }
    });
  }, [tailoringStatus, token]);

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(filteredStudents.map(student => student.id));
    }
  };

  const handleStudentToggle = (studentId: number) => {
    setSelectedStudents(prev => 
      prev.includes(studentId)
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const handleAssign = async () => {
    if (selectedStudents.length === 0 || !selectedClass) return;
    
    setLoading(true);
    try {
      const assignmentData = {
        student_ids: selectedStudents,
        class_id: selectedClass,
        due_date: dueDate ? new Date(dueDate).toISOString() : null,
        assignment_type: assignmentType,
      };
      
      await onAssign(assignmentData);
      
      const className = classes.find(c => c.id === selectedClass)?.name || '';
      setAssignmentSuccess({
        studentCount: selectedStudents.length,
        className,
      });
    } catch (error) {
      console.error('Error assigning document:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePreviewTailored = async (studentId: number) => {
    if (!token || !document) return;
    
    const tailoredDoc = tailoringStatus[studentId]?.tailored_document;
    if (!tailoredDoc || !tailoredDoc.id) return;
    
    try {
      const response = await fetch(`/api/tailored-documents/${tailoredDoc.id}/download`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) throw new Error('Failed to get preview URL');
      
      const data = await response.json();
      if (data.success && data.output_format === 'interactive' && data.pages) {
        setInteractivePreviewPages(data.pages);
        setInteractivePreviewName(data.document_name || document.name);
        setInteractivePreviewOpen(true);
      } else if (data.success && data.download_url) {
        window.open(data.download_url, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      console.error('Error previewing tailored document:', error);
      alert('Failed to open preview. Please try again.');
    }
  };

  const handleDeleteTailored = async (studentId: number) => {
    if (!token || !document) return;
    
    try {
      const response = await fetch(`/api/documents/${document.id}/tailored/${studentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) throw new Error('Failed to delete tailored document');
      
      const data = await response.json();
      if (data.success) {
        // Update the tailoring status
        setTailoringStatus(prev => ({
          ...prev,
          [studentId]: {
            ...prev[studentId],
            has_tailored: false,
            tailored_document: null
          }
        }));
        
        // Refresh tailoring status
        if (selectedClass) {
          await fetchTailoringStatus(selectedClass);
        }
      }
    } catch (error) {
      console.error('Error deleting tailored document:', error);
    }
  };

  const handleClose = () => {
    setSelectedClass(null);
    setClassStudents([]);
    setSelectedStudents([]);
    setDueDate('');
    setAssignmentType('submittable');
    setSearchQuery('');
    setSelectedCultures([]);
    setSelectAll(false);
    setTailorDialogOpen(false);
    setSelectedStudentForTailor(null);
    setBulkTailorDialogOpen(false);
    setTailoringStatus({});
    setLoadingTailoringStatus(false);
    setThumbnails({});
    setLoadingThumbnails({});
    setExistingAssignment(null);
    setAssignmentSuccess(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={assignmentSuccess ? 'sm' : 'lg'}
      fullWidth
      fullScreen={isMobile && !assignmentSuccess}
      className="assignment-dialog"
      aria-labelledby="assignment-dialog-title"
    >
      {assignmentSuccess ? (
        <>
          <DialogContent sx={{ textAlign: 'center', py: 5, px: 4 }}>
            <CheckCircleIcon sx={{ fontSize: 64, color: '#2e7d32', mb: 2 }} />
            <Typography variant="h5" fontWeight={600} gutterBottom>
              {t('assignmentDialog.successTitle', 'Assignment Created!')}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {t('assignmentDialog.successDetail', 
                '"{{documentName}}" has been assigned to {{count}} student{{plural}} in {{className}}.', {
                documentName: document?.name,
                count: assignmentSuccess.studentCount,
                plural: assignmentSuccess.studentCount !== 1 ? 's' : '',
                className: assignmentSuccess.className,
              })}
            </Typography>
            {dueDate && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {t('assignmentDialog.successDueDate', 'Due: {{date}}', {
                  date: new Date(dueDate).toLocaleDateString(undefined, { 
                    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' 
                  })
                })}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              {t('assignmentDialog.successVisibility', 'Students will see this assignment in their portal.')}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
            <Button
              variant="contained"
              onClick={handleClose}
              size="large"
              sx={{ minWidth: 160 }}
              aria-label={t('common.done', 'Done')}
            >
              {t('common.done', 'Done')}
            </Button>
          </DialogActions>
        </>
      ) : (
      <>
      <DialogTitle id="assignment-dialog-title">
        Assign Document: {document?.name}
      </DialogTitle>
      
      <DialogContent sx={{ p: 0 }}>
        {existingAssignment && (
          <Alert 
            severity="info" 
            sx={{ mx: 3, mt: 2 }}
            aria-label={t('assignmentDialog.existingAssignmentWarning', 'Existing assignment warning')}
          >
            <Typography variant="body2">
              <strong>{t('assignmentDialog.alreadyAssigned', 'Already assigned:')}</strong>{' '}
              {t('assignmentDialog.alreadyAssignedDetail', 
                'This document is currently assigned to {{className}} ({{count}} students). Saving will update the existing assignment.', 
                { className: existingAssignment.class_name, count: existingAssignment.student_count }
              )}
            </Typography>
          </Alert>
        )}
        {/* Class Selection and Due Date */}
        <Box sx={{ p: 3, borderBottom: '1px solid #e0e0e0' }}>
          <FormControl fullWidth margin="normal">
            <InputLabel>Select Class</InputLabel>
            <Select
              value={selectedClass || ''}
              onChange={(e) => setSelectedClass(Number(e.target.value))}
              label="Select Class"
              required
            >
              {classes.map((classData) => (
                <MenuItem key={classData.id} value={classData.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <Box sx={{ flex: 1 }}>
                      {classData.name}
                      {classData.subject && ` - ${classData.subject}`}
                      {classData.grade_level && ` (${classData.grade_level})`}
                    </Box>
                    <Chip 
                      label={classData.google_classroom_id ? 'Google Classroom' : 'Standalone'}
                      size="small"
                      color={classData.google_classroom_id ? 'primary' : 'default'}
                      sx={{ 
                        fontSize: '0.7rem',
                        height: '20px',
                        mr: 1
                      }}
                    />
                    <Typography variant="caption" sx={{ color: '#666', whiteSpace: 'nowrap' }}>
                      {classData.student_count} students
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          {selectedClass && (
            <Box sx={{ 
              mt: 2, 
              p: 2, 
              bgcolor: classes.find(c => c.id === selectedClass)?.google_classroom_id ? '#e3f2fd' : '#fff3e0',
              borderRadius: 1,
              border: '1px solid',
              borderColor: classes.find(c => c.id === selectedClass)?.google_classroom_id ? '#90caf9' : '#ffb74d'
            }}>
              <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {classes.find(c => c.id === selectedClass)?.google_classroom_id ? (
                  <>
                    <CheckCircleIcon fontSize="small" color="primary" />
                    <strong>Google Classroom Synced:</strong> Assignments will be created in both Classroom Bridge and Google Classroom
                  </>
                ) : (
                  <>
                    <AssignmentIcon fontSize="small" sx={{ color: '#e65100' }} />
                    <strong>Standalone Class:</strong> Assignments will only be created in Classroom Bridge
                  </>
                )}
              </Typography>
            </Box>
          )}
          
          <TextField
            label="Due Date (Optional)"
            type="datetime-local"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            fullWidth
            margin="normal"
            helperText="Leave empty for no due date"
            InputLabelProps={{
              shrink: true,
            }}
          />

          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
              {t('assignmentDialog.assignmentType', 'Assignment Type')}
            </Typography>
            <ToggleButtonGroup
              value={assignmentType}
              exclusive
              onChange={(_, value) => { if (value) setAssignmentType(value); }}
              size="small"
              fullWidth
              aria-label={t('assignmentDialog.assignmentType', 'Assignment Type')}
            >
              <ToggleButton value="submittable" sx={{ textTransform: 'none', gap: 1 }}>
                <SendIcon fontSize="small" />
                {t('assignmentDialog.submittable', 'Submittable')}
              </ToggleButton>
              <ToggleButton value="informational" sx={{ textTransform: 'none', gap: 1 }}>
                <MenuBookIcon fontSize="small" />
                {t('assignmentDialog.informational', 'Informational')}
              </ToggleButton>
            </ToggleButtonGroup>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {assignmentType === 'submittable'
                ? t('assignmentDialog.submittableHint', 'Students will be able to submit work for this assignment.')
                : t('assignmentDialog.informationalHint', 'Read-only material. Students can view the document and take quizzes, but won\'t see a Submit Work option.')}
            </Typography>
          </Box>
        </Box>

        {/* Student Selection Interface */}
        {selectedClass && (
          <>
            {/* Search and Filter Bar */}
            <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0', bgcolor: '#fafafa' }}>
              <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <TextField
                  placeholder="Search students by name, ID, or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  variant="outlined"
                  size="small"
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ color: '#666', mr: 1 }} />
                  }}
                  sx={{ 
                    flex: 1, 
                    minWidth: '200px',
                    maxWidth: '400px'
                  }}
                />
                
                <Autocomplete
                  multiple
                  options={cultures}
                  getOptionLabel={(option) => option.name}
                  value={selectedCultures}
                  onChange={(_, newValue) => setSelectedCultures(newValue)}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        variant="outlined"
                        label={option.name}
                        size="small"
                        {...getTagProps({ index })}
                        key={option.id}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      size="small"
                      placeholder="Filter by culture..."
                    />
                  )}
                  sx={{ 
                    minWidth: '200px',
                    maxWidth: '300px',
                    flex: 1
                  }}
                />

                {/* Bulk Tailor Button */}
                {selectedStudents.length >= 2 && (
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<TailorIcon />}
                    onClick={() => setBulkTailorDialogOpen(true)}
                    disabled={
                      selectedStudents.every(
                        (sid) => tailoringStatus[sid]?.has_tailored
                      )
                    }
                    sx={{
                      textTransform: 'none',
                      whiteSpace: 'nowrap',
                      alignSelf: 'center',
                    }}
                    aria-label={t('bulkTailoring.tailorSelected')}
                  >
                    {t('bulkTailoring.tailorSelected')} ({selectedStudents.length})
                  </Button>
                )}
              </Box>
            </Box>

            {/* Student List */}
            <Box sx={{ height: isMobile ? 'auto' : '400px', maxHeight: isMobile ? '50vh' : '400px', overflow: 'auto' }}>
              {filteredStudents.length === 0 ? (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary">
                    {searchQuery || selectedCultures.length > 0 
                      ? 'No students match your filters' 
                      : 'No students in selected class'
                    }
                  </Typography>
                </Box>
              ) : isMobile ? (
                /* Mobile: card-based student list */
                <Box sx={{ px: 2, py: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, px: 0.5 }}>
                    <Checkbox
                      checked={selectAll}
                      onChange={handleSelectAll}
                      disabled={filteredStudents.length === 0}
                      aria-label="Select all students"
                    />
                    <Typography variant="subtitle2" fontWeight={600}>
                      Student ({filteredStudents.length})
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {filteredStudents.map((student) => (
                      <Paper
                        key={student.id}
                        elevation={0}
                        onClick={() => handleStudentToggle(student.id)}
                        sx={{
                          p: 1.5,
                          border: '1px solid',
                          borderColor: selectedStudents.includes(student.id) ? 'var(--cb-primary)' : '#e0e0e0',
                          borderRadius: 2,
                          bgcolor: selectedStudents.includes(student.id) ? 'rgba(47, 158, 122, 0.04)' : '#fff',
                          cursor: 'pointer',
                          transition: 'all 0.15s ease',
                        }}
                      >
                        {/* Row 1: Checkbox + Name */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Checkbox
                            checked={selectedStudents.includes(student.id)}
                            onChange={() => handleStudentToggle(student.id)}
                            onClick={(e) => e.stopPropagation()}
                            size="small"
                            sx={{ p: 0.5 }}
                          />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="subtitle2" fontWeight={600} noWrap>
                              {student.full_name}
                            </Typography>
                            {(student.student_id || student.email) && (
                              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                                {student.student_id && `ID: ${student.student_id}`}
                                {student.student_id && student.email && ' • '}
                                {student.email}
                              </Typography>
                            )}
                          </Box>
                        </Box>

                        {/* Row 2: Grade, Reading Level, Cultures */}
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', ml: 4.5, mb: 1 }}>
                          {student.grade_level && (
                            <Chip size="small" label={student.grade_level} sx={{ height: 22, fontSize: '0.7rem' }} />
                          )}
                          {student.reading_level && (
                            <Chip size="small" label={`RL ${student.reading_level}`} variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />
                          )}
                          {student.cultures.slice(0, 2).map((culture, index) => (
                            <Chip
                              key={index}
                              label={culture}
                              size="small"
                              variant="outlined"
                              sx={{ height: 22, fontSize: '0.65rem', maxWidth: 100, '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }}
                            />
                          ))}
                          {student.cultures.length > 2 && (
                            <Chip label={`+${student.cultures.length - 2}`} size="small" variant="outlined" sx={{ height: 22, fontSize: '0.65rem' }} />
                          )}
                        </Box>

                        {/* Row 3: Tailor actions */}
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', ml: 4.5 }}>
                          {tailoringStatus[student.id]?.has_tailored ? (
                            <>
                              <Box
                                onClick={(e) => { e.stopPropagation(); handlePreviewTailored(student.id); }}
                                sx={{
                                  width: 48, height: 64, border: '2px solid #e0e0e0', borderRadius: 1,
                                  overflow: 'hidden', cursor: 'pointer', display: 'flex', alignItems: 'center',
                                  justifyContent: 'center', bgcolor: '#f5f5f5', position: 'relative', flexShrink: 0,
                                }}
                                aria-label={t('assignmentDialog.previewTailored', { name: student.full_name })}
                              >
                                {thumbnails[student.id] ? (
                                  <img src={thumbnails[student.id]!} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top' }} />
                                ) : (
                                  <VisibilityIcon sx={{ color: '#666', fontSize: '1rem' }} />
                                )}
                                <CheckCircleIcon sx={{ position: 'absolute', top: -3, right: -3, fontSize: '0.85rem', color: '#4caf50', bgcolor: '#fff', borderRadius: '50%' }} />
                              </Box>
                              <Chip label={t('assignmentDialog.tailored', 'Tailored')} size="small" color="success" sx={{ height: 24, fontSize: '0.7rem' }} />
                              <Button size="small" variant="outlined" color="error"
                                onClick={(e) => { e.stopPropagation(); handleDeleteTailored(student.id); }}
                                sx={{ minWidth: 'auto', px: 1, height: 28, ml: 'auto' }}
                                aria-label={t('assignmentDialog.deleteTailoredFor', { name: student.full_name })}
                              >
                                <DeleteIcon fontSize="small" />
                              </Button>
                            </>
                          ) : (
                            <Button size="small" variant="outlined" startIcon={<TailorIcon />}
                              onClick={(e) => { e.stopPropagation(); setSelectedStudentForTailor(student); setTailorDialogOpen(true); }}
                              sx={{ textTransform: 'none' }}
                              aria-label={t('assignmentDialog.tailorFor', { name: student.full_name })}
                            >
                              {t('assignmentDialog.tailor')}
                            </Button>
                          )}
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                </Box>
              ) : (
                /* Desktop: table layout */
                <TableContainer component={Paper} elevation={0} sx={{ width: '100%', overflowX: 'hidden' }}>
                  <Table stickyHeader sx={{ tableLayout: 'auto', width: '100%' }}>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox" sx={{ width: '48px', minWidth: '48px' }}>
                          <Checkbox
                            checked={selectAll}
                            onChange={handleSelectAll}
                            disabled={filteredStudents.length === 0}
                          />
                        </TableCell>
                        <TableCell sx={{ minWidth: '180px' }}>
                          <Typography variant="subtitle2" fontWeight={600}>
                            Student ({filteredStudents.length})
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ minWidth: '70px', width: '70px' }}>Grade</TableCell>
                        <TableCell sx={{ minWidth: '90px', width: '90px' }}>{t('classWork.readingLevel')}</TableCell>
                        <TableCell sx={{ minWidth: '110px' }}>Cultures</TableCell>
                        <TableCell sx={{ minWidth: '140px', width: '140px' }}>{t('assignmentDialog.actionsColumn')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredStudents.map((student) => (
                        <TableRow 
                          key={student.id}
                          hover
                          selected={selectedStudents.includes(student.id)}
                          onClick={() => handleStudentToggle(student.id)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedStudents.includes(student.id)}
                              onChange={() => handleStudentToggle(student.id)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="subtitle2" fontWeight={500}>
                                {student.full_name}
                              </Typography>
                              {(student.student_id || student.email) && (
                                <Typography variant="caption" color="text.secondary">
                                  {student.student_id && `ID: ${student.student_id}`}
                                  {student.student_id && student.email && ' • '}
                                  {student.email}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            {student.grade_level && (
                              <Chip size="small" label={student.grade_level} />
                            )}
                          </TableCell>
                          <TableCell>
                            {student.reading_level && (
                              <Typography variant="body2">
                                Level {student.reading_level}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', maxWidth: '100%' }}>
                              {student.cultures.slice(0, 2).map((culture, index) => (
                                <Chip
                                  key={index}
                                  label={culture}
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontSize: '0.7rem', height: '20px', maxWidth: '100px', '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }}
                                />
                              ))}
                              {student.cultures.length > 2 && (
                                <Tooltip title={student.cultures.slice(2).join(', ')}>
                                  <Chip label={`+${student.cultures.length - 2}`} size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: '20px' }} />
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'nowrap' }}>
                              {tailoringStatus[student.id]?.has_tailored ? (
                                <>
                                  <Tooltip title={t('assignmentDialog.clickToPreview')}>
                                    <Box
                                      onClick={(e) => { e.stopPropagation(); handlePreviewTailored(student.id); }}
                                      sx={{
                                        width: 60, height: 80, border: '2px solid #e0e0e0', borderRadius: 1,
                                        overflow: 'hidden', cursor: 'pointer', display: 'flex', alignItems: 'center',
                                        justifyContent: 'center', bgcolor: '#f5f5f5', transition: 'all 0.2s ease',
                                        position: 'relative',
                                        '&:hover': { borderColor: '#1976d2', transform: 'scale(1.05)', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' },
                                        '&:hover .thumbnail-overlay': { opacity: 1 }
                                      }}
                                      aria-label={t('assignmentDialog.previewTailored', { name: student.full_name })}
                                    >
                                      {loadingThumbnails[student.id] ? (
                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>{t('common.loading')}</Typography>
                                      ) : thumbnails[student.id] ? (
                                        <>
                                          <img src={thumbnails[student.id]!} alt={t('assignmentDialog.thumbnailAlt', { name: student.full_name })} style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top' }} />
                                          <Box className="thumbnail-overlay" sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, bgcolor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0, transition: 'opacity 0.2s ease' }}>
                                            <VisibilityIcon sx={{ color: '#fff', fontSize: '1.2rem' }} />
                                          </Box>
                                        </>
                                      ) : (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
                                          <VisibilityIcon sx={{ color: '#666', fontSize: '1.2rem', mb: 0.5 }} />
                                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>{t('assignmentDialog.view')}</Typography>
                                        </Box>
                                      )}
                                      <CheckCircleIcon sx={{ position: 'absolute', top: -4, right: -4, fontSize: '1rem', color: '#4caf50', bgcolor: '#fff', borderRadius: '50%' }} />
                                    </Box>
                                  </Tooltip>
                                  <Tooltip title={t('assignmentDialog.deleteTailored')}>
                                    <Button size="small" variant="outlined" color="error"
                                      onClick={(e) => { e.stopPropagation(); handleDeleteTailored(student.id); }}
                                      sx={{ minWidth: 'auto', px: 1, fontSize: '0.75rem', height: 28 }}
                                      aria-label={t('assignmentDialog.deleteTailoredFor', { name: student.full_name })}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </Button>
                                  </Tooltip>
                                </>
                              ) : (
                                <Button size="small" variant="outlined" startIcon={<TailorIcon />}
                                  onClick={(e) => { e.stopPropagation(); setSelectedStudentForTailor(student); setTailorDialogOpen(true); }}
                                  sx={{ minWidth: 'auto', px: 1 }}
                                  aria-label={t('assignmentDialog.tailorFor', { name: student.full_name })}
                                >
                                  {t('assignmentDialog.tailor')}
                                </Button>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>

          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e0e0e0', bgcolor: '#fafafa' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
          <Box sx={{ flex: 1 }}>
            {selectedStudents.length > 0 && (
              <Typography variant="body2" color="text.secondary">
                {selectedStudents.length} student{selectedStudents.length !== 1 ? 's' : ''} selected
              </Typography>
            )}
          </Box>
          <Button onClick={handleClose} disabled={loading}>
            {t('modals.cancel')}
          </Button>
          <Button 
            onClick={handleAssign}
            variant="contained"
            disabled={selectedStudents.length === 0 || !selectedClass || loading}
            sx={{ ml: 1 }}
          >
            {loading 
              ? t('modals.creatingAssignment')
              : `Assign to ${selectedStudents.length} Student${selectedStudents.length !== 1 ? 's' : ''}`
            }
          </Button>
        </Box>
      </DialogActions>
      
      </>
      )}

      {/* Tailor Assignment Dialog */}
      <TailorAssignmentDialog
        open={tailorDialogOpen}
        onClose={() => {
          setTailorDialogOpen(false);
          setSelectedStudentForTailor(null);
        }}
        document={document}
        student={selectedStudentForTailor}
        onSuccess={() => {
          if (selectedClass) {
            fetchTailoringStatus(selectedClass);
          }
          setTailorDialogOpen(false);
          setSelectedStudentForTailor(null);
        }}
      />

      {/* Bulk Tailor Dialog */}
      <BulkTailorDialog
        open={bulkTailorDialogOpen}
        onClose={() => setBulkTailorDialogOpen(false)}
        document={document}
        students={classStudents.filter((s) => selectedStudents.includes(s.id))}
        tailoringStatus={tailoringStatus}
        onSuccess={() => {
          if (selectedClass) {
            fetchTailoringStatus(selectedClass);
          }
          setBulkTailorDialogOpen(false);
        }}
      />

      {/* Interactive Document Preview Dialog */}
      <Dialog
        open={interactivePreviewOpen}
        onClose={() => setInteractivePreviewOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        aria-label={t('assignmentDialog.interactivePreview', 'Interactive Document Preview')}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
          <Typography variant="h6" component="span" noWrap sx={{ flex: 1, mr: 1 }}>
            {interactivePreviewName}
          </Typography>
          <Chip label={t('assignmentDialog.interactiveLabel', 'Interactive / Fillable')} size="small" color="info" sx={{ mr: 1, flexShrink: 0 }} />
          <IconButton onClick={() => setInteractivePreviewOpen(false)} aria-label={t('common.close', 'Close')}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, bgcolor: '#e0e0e0' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2, alignItems: 'center' }}>
            {interactivePreviewPages.map((page: any) => (
              <Paper
                key={page.page_number}
                elevation={3}
                sx={{ position: 'relative', width: '100%', maxWidth: 800, overflow: 'hidden' }}
              >
                {page.background_image_url ? (
                  <Box sx={{ position: 'relative' }}>
                    <img
                      src={page.background_image_url}
                      alt={`${t('assignmentDialog.pageLabel', 'Page')} ${page.page_number}`}
                      style={{ width: '100%', display: 'block' }}
                    />
                    {(page.fields || []).map((field: any) => (
                      <Box
                        key={field.id}
                        sx={{
                          position: 'absolute',
                          left: `${field.x_pct}%`,
                          top: `${field.y_pct}%`,
                          width: `${field.width_pct}%`,
                          height: `${field.height_pct}%`,
                          border: '1.5px dashed rgba(25, 118, 210, 0.6)',
                          borderRadius: '3px',
                          bgcolor: 'rgba(25, 118, 210, 0.06)',
                          display: 'flex',
                          alignItems: 'flex-start',
                          px: 0.5,
                          pointerEvents: 'none',
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'rgba(25, 118, 210, 0.8)',
                            fontSize: '0.6rem',
                            lineHeight: 1.2,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: '100%',
                          }}
                        >
                          {field.label}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Typography color="text.secondary">
                      {t('assignmentDialog.pageImageUnavailable', 'Page image unavailable')}
                    </Typography>
                  </Box>
                )}
                <Box sx={{ px: 2, py: 0.5, bgcolor: '#f5f5f5', borderTop: '1px solid #e0e0e0' }}>
                  <Typography variant="caption" color="text.secondary">
                    {t('assignmentDialog.pageLabel', 'Page')} {page.page_number}
                    {page.fields?.length > 0 && ` \u2022 ${page.fields.length} ${t('assignmentDialog.fieldsLabel', 'fields')}`}
                  </Typography>
                </Box>
              </Paper>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={() => setInteractivePreviewOpen(false)} variant="contained">
            {t('common.close', 'Close')}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default AssignmentDialog;
